import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Tooltip } from '@repo/design-system';
import { space, typeRole, weight } from '@repo/design-tokens';
import { visuallyHidden } from '@repo/ui';

import { CALIBRATION_LOOK } from './calibrationLook';
import type { CalibrationStatus } from './calibrationStatusText';

export interface CalibrationRowProps {
  status: CalibrationStatus;
  /** Calibrate or Recalibrate pressed while allowed. */
  onCalibrate: () => void;
  /** Remove pressed: the panel removes the calibration and says so. */
  onRemove: () => void;
  /** The Calibrate button, so focus can move to it once Remove has gone. */
  calibrateRef: React.Ref<HTMLButtonElement>;
}

/**
 * The camera's calibration on one line (`unified-camera-panel`, R3): the status dot, its title and,
 * on the right, Calibrate or Recalibrate and (where allowed) Remove. The status's sentence is not a
 * second line: it is the buttons' accessible description and a tooltip on the title. The buttons do
 * exactly what the status box's did.
 */
const CalibrationRow: React.FC<CalibrationRowProps> = ({
  status,
  onCalibrate,
  onRemove,
  calibrateRef,
}) => {
  const subId = useId();
  const titleRef = useRef<HTMLSpanElement | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const showTip = (on: boolean) => {
    const el = titleRef.current;
    setTip(on && el ? { x: el.offsetLeft + el.offsetWidth / 2, y: el.offsetTop } : null);
  };
  const look = CALIBRATION_LOOK.row[status.state];
  return (
    <Row data-testid="calibration-status" data-state={status.state}>
      <Dot style={{ background: look.dot }} aria-hidden="true" />
      <Title
        ref={titleRef}
        style={{ color: look.ink }}
        onMouseEnter={() => showTip(true)}
        onMouseLeave={() => showTip(false)}
      >
        {status.title}
      </Title>
      <span id={subId} style={visuallyHidden}>
        {status.sub}
      </span>
      <Actions>
        <Button
          ref={calibrateRef}
          variant={status.state === 'calibrated' ? 'secondary' : 'primary'}
          size="xs"
          aria-describedby={subId}
          aria-disabled={status.disabled}
          onClick={() => {
            if (!status.disabled) onCalibrate();
          }}
        >
          {status.action}
        </Button>
        {status.removable ? (
          <Button
            variant="text"
            size="xs"
            aria-label="Remove calibration"
            aria-describedby={subId}
            onClick={onRemove}
          >
            Remove
          </Button>
        ) : null}
      </Actions>
      <Tooltip
        open={tip !== null}
        anchor={tip ?? { x: 0, y: 0 }}
        placement="top"
        title={status.sub}
      />
    </Row>
  );
};

export default CalibrationRow;

const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${space[2]};
  min-height: 28px;
`;

const Dot = styled.span`
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: 50%;
`;

const Title = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${typeRole.body2.fontSize};
  line-height: ${typeRole.body2.lineHeight};
  font-weight: ${weight.semibold};
`;

const Actions = styled.span`
  flex: none;
  display: flex;
  gap: ${space[1]};
`;
